import { useEffect, useState } from "react";
import { fetchTargetRole, clearTargetRole, fetchPromotionReadiness } from "../lib/api";
import { Target, X, Sparkles } from "lucide-react";

/**
 * TargetRoleBanner — Persistent header shown across the Kariyerim experience once a target role is pinned.
 * Displays: 🎯 Hedef: {role} · %X hazır · kalan kriter sayısı · "Değiştir/Kaldır".
 */
export default function TargetRoleBanner({ employeeId, onChanged, onSelectTarget }) {
  const [target, setTarget] = useState(null);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchTargetRole(employeeId).then((d) => {
      if (!mounted) return;
      setTarget(d);
      if (d?.target_role_id) {
        fetchPromotionReadiness(employeeId, d.target_role_id).then((r) => mounted && setReadiness(r)).catch(() => {});
      } else {
        setReadiness(null);
      }
    });
    return () => { mounted = false; };
  }, [employeeId, onChanged]);

  if (!target?.target_role) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-center justify-between gap-3" data-testid="target-role-banner-empty">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span>Henüz bir hedef rol seçmedin. Kariyer Ağacı&apos;ndan bir rolü hedef yap → tüm ekran sana özelleşsin.</span>
        </div>
        {onSelectTarget && (
          <button type="button" onClick={onSelectTarget} className="text-sm font-medium text-sky-700 hover:underline" data-testid="target-role-select-cta">
            Hedef seç →
          </button>
        )}
      </div>
    );
  }

  const r = readiness || {};
  const pct = r.readiness_pct || 0;
  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-emerald-50 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="target-role-banner-pinned">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
          <Target className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Hedefim</div>
          <div className="text-sm font-semibold text-slate-800">{target.target_role.title}</div>
          <div className="text-[11px] text-slate-500">{target.target_role.department} · {target.target_role.career_band}</div>
        </div>
      </div>
      <div className="flex-1 md:pl-6">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-emerald-700">%{pct}</span>
          <span className="text-xs text-slate-500">hazır</span>
          <span className="text-xs text-slate-400">· {r.criteria_met || 0}/{r.criteria_total || 0} kriter · {r.mandatory_met || 0}/{r.mandatory_total || 0} zorunlu</span>
        </div>
        <div className="h-2 mt-1 w-full bg-white/60 rounded-full overflow-hidden border border-slate-100">
          <div className="h-full bg-gradient-to-r from-emerald-400 to-sky-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <button
        type="button"
        onClick={async () => { await clearTargetRole(employeeId); onChanged?.(); }}
        className="text-xs text-slate-500 hover:text-rose-600 flex items-center gap-1 self-start md:self-center"
        title="Hedefi kaldır"
        data-testid="target-role-clear"
      >
        <X className="w-3.5 h-3.5" /> Kaldır
      </button>
    </div>
  );
}
